import { inject, injectable } from 'inversify';
import { CLIENT_EVENTS } from '../../constants/events';
import EnhancedEventEmitter from '../../EnhancedEventEmitter';
import { TOKEN } from '../../inversify.tokens';
import { AvailableMediaDevices, DeviceErrors } from '../../types/common';
import Logger from '../Logger';

const isUnexpectedError = (errorMessage: string): boolean => {
  const errors: string[] = [
    DeviceErrors.NotFoundError,
    DeviceErrors.NotAllowedError,
    DeviceErrors.DeviceIsBusy,
  ];

  return !errors.includes(errorMessage);
};

const getDeviceErrorCode = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const maybeMessage = (error as { message?: unknown }).message;

    return typeof maybeMessage === 'string' ? maybeMessage : undefined;
  }

  return undefined;
};

@injectable()
class System {
  public isEnableVideoDevicesLock = false;

  public isEnableAudioDevicesLock = false;

  public isDevicesDetected = false;

  public availableVideoDevices: MediaDeviceInfo[] = [];

  public availableAudioDevices: MediaDeviceInfo[] = [];

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = loggerFactory('System');

    this.deviceChangeHandler = this.deviceChangeHandler.bind(this);

    this.listenDevices();
  }

  setIsEnableVideoDevicesLock(value: boolean): void {
    this.isEnableVideoDevicesLock = value;
  }

  setIsEnableAudioDevicesLock(value: boolean): void {
    this.isEnableAudioDevicesLock = value;
  }

  setAvailableVideoDevices(availableVideoDevices: MediaDeviceInfo[]): void {
    this.availableVideoDevices = availableVideoDevices;
  }

  setAvailableAudioDevices(availableAudioDevices: MediaDeviceInfo[]): void {
    this.availableAudioDevices = availableAudioDevices;
  }

  async requestMediaDevicesAccess(
    constraints: MediaStreamConstraints = { video: true, audio: true },
  ): Promise<MediaStreamTrack[]> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      return stream.getTracks();
    } catch (error) {
      this.#logger.error('getUserMedia()', { error, constraints });

      const errorName = error instanceof Error ? error.name : undefined;

      if (errorName === 'NotAllowedError') {
        throw new Error(DeviceErrors.NotAllowedError);
      }

      if (errorName === 'NotReadableError') {
        throw new Error(DeviceErrors.DeviceIsBusy);
      }

      if (errorName === 'NotFoundError') {
        throw new Error(DeviceErrors.NotFoundError);
      }

      throw new Error(DeviceErrors.RequestDevicesUnknownError);
    }
  }

  async hasAvailableMediaDevicesList(): Promise<boolean> {
    const devices = await navigator.mediaDevices.enumerateDevices();

    if (devices.length === 0) {
      return true;
    }

    /* this checks on browser level, will succeed if allowed in browser, but denied by OS */
    const hasDevicesList = devices.every((device) => device.deviceId && device.label);

    return hasDevicesList;
  }

  async refreshAvailableMediaDevicesList(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = [] as MediaDeviceInfo[];
    const audioDevices = [] as MediaDeviceInfo[];
    devices.forEach((device) => {
      if (device.deviceId === '') {
        return;
      }

      if (device.kind === 'videoinput') {
        videoDevices.push(device);
      }

      if (device.kind === 'audioinput') {
        audioDevices.push(device);
      }
    });

    this.setAvailableVideoDevices(videoDevices);
    this.setAvailableAudioDevices(audioDevices);

    const mapDeviceInfo = ({ label, kind }: MediaDeviceInfo): { label: string; kind: MediaDeviceKind } => ({
      label,
      kind,
    });

    this.#logger.info('Devices list updated', {
      videoDevices: videoDevices.map(mapDeviceInfo),
      audioDevices: audioDevices.map(mapDeviceInfo),
    });

    return devices;
  }

  async detectDevices(force?: boolean): Promise<void> {
    if (this.isDevicesDetected && !force) {
      return;
    }

    const hasAvailableMediaDevicesList = await this.hasAvailableMediaDevicesList();

    if (!hasAvailableMediaDevicesList) {
      await this.tryToAccessDevices();
    }

    // at this moment an access to devices were granted or no devices exist
    this.isDevicesDetected = true;

    const rawDevices = await this.refreshAvailableMediaDevicesList();
    const hasAudioInputDevices = rawDevices.some((x) => x.kind === 'audioinput');
    const hasVideoInputDevices = rawDevices.some((x) => x.kind === 'videoinput');

    if (!hasAudioInputDevices && !hasVideoInputDevices) {
      throw new Error(DeviceErrors.NoDevices);
    }
  }

  async deviceChangeHandler(): Promise<void> {
    if (!this.isDevicesDetected) {
      return;
    }

    await this.refreshAvailableMediaDevicesList();

    const payload: AvailableMediaDevices = {
      audio: this.availableAudioDevices,
      video: this.availableVideoDevices,
    };
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.devicesListUpdated, payload);
  }

  listenDevices(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  private async assertHasAccessToMediaDevices(
    constraints: MediaStreamConstraints = { video: true, audio: true },
  ): Promise<void> {
    const tracks = await this.requestMediaDevicesAccess(constraints);
    tracks.forEach((track) => track.stop());
  }

  private async tryToAccessDevices(): Promise<void> {
    try {
      const tracks = await this.requestMediaDevicesAccess();
      tracks.forEach((track) => track.stop());
    } catch (error) {
      const errorCode = getDeviceErrorCode(error);

      if (!errorCode || isUnexpectedError(errorCode)) {
        throw error;
      }

      /* if device is busy that means we have permissions */
      if (errorCode !== DeviceErrors.DeviceIsBusy) {
        await this.assertHasAccessToMediaDevices({ audio: true });
        await this.assertHasAccessToMediaDevices({ video: true });
      }
    }
  }
}

export default System;
